import { useCallback, useEffect, useMemo, useState } from 'react';
import { useControlsStore } from '../../store/controlsSlice';
import {
  CONTROLS_CONFIG,
  DungeonAction,
  GAMEPAD_BUTTON_LABELS,
  GameplayContext,
  INPUT_PRESETS,
  InputPresetId,
  InterfaceAction,
} from '../../data/controls.config';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { useAudioStore } from '../../store/audioSlice';
import { type RawInput } from '../../services/InputService';
import { useInputCapture } from '../../hooks/useInput';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { closeInterface, openInterface } from '../../services/InterfaceService';
import { useGameStore } from '../../store/gameSlice';
import { useInputSourceStore } from '../../store/inputSourceSlice';
import { KeyDisplay } from './KeyDisplay';
import { NineSliceFrame } from './NineSliceFrame';
import { TrackBar } from './TrackBar';
import './OptionsMenu.scss';
import { GamepadBinding, KeyboardBinding } from '../../types/types';

type OptionsTab = 'general' | 'sound' | 'controls';

type CapturingTarget = {
  context: GameplayContext;
  action: string;
  inputType: 'keyboard' | 'gamepad';
};

const TABS_IN_GAME: OptionsTab[] = ['general', 'sound', 'controls'];
const TABS_MAIN_MENU: OptionsTab[] = ['sound', 'controls'];

const VOLUME_STEP = 0.05;

// Read-only listing of the dev-mode hotkeys (registered in useDevtoolHotkeys).
// Shown in the General tab when Dev mode is on — navigable for scrolling, no
// action on confirm.
const DEV_HOTKEYS: { keys: string; label: string }[] = [
  { keys: 'F1', label: 'Pause / reprise' },
  { keys: 'F2', label: 'Panneau Devtool' },
  { keys: 'F3', label: 'God mode' },
  { keys: 'F4', label: 'No-clip' },
  { keys: 'F5', label: 'Console' },
  { keys: 'F6', label: 'Liste des objets' },
  { keys: 'Ctrl + E', label: 'Étage suivant' },
  { keys: 'Ctrl + I', label: 'One-hit KO' },
  { keys: 'Ctrl + U', label: 'Révéler la map' },
  { keys: 'F11 + F12', label: 'Réinitialiser la position des popups' },
];

export const OptionsMenu = () => {
  const mapping = useControlsStore();
  const applyPreset = useControlsStore((s) => s.applyPreset);
  const mouseControlEnabled = useControlsStore((s) => s.mouseControlEnabled);
  const devModeEnabled = useDevtoolStore((s) => s.devModeEnabled);
  const setDevModeEnabled = useDevtoolStore((s) => s.setDevModeEnabled);
  const showTileIndex = useDevtoolStore((s) => s.showTileIndex);
  const setShowTileIndex = useDevtoolStore((s) => s.setShowTileIndex);
  const showChessboard = useDevtoolStore((s) => s.showChessboard);
  const setShowChessboard = useDevtoolStore((s) => s.setShowChessboard);
  const showCoordinates = useDevtoolStore((s) => s.showCoordinates);
  const setShowCoordinates = useDevtoolStore((s) => s.setShowCoordinates);
  const masterVolume = useAudioStore((s) => s.masterVolume);
  const musicVolume = useAudioStore((s) => s.musicVolume);
  const sfxVolume = useAudioStore((s) => s.sfxVolume);
  const setMasterVolume = useAudioStore((s) => s.setMasterVolume);
  const setMusicVolume = useAudioStore((s) => s.setMusicVolume);
  const setSfxVolume = useAudioStore((s) => s.setSfxVolume);
  const gameplay = useGameStore((s) => s.gameplay);
  // When opened from the MainMenu, hide the "General" tab — it only holds dev
  // toggles (devtool overlay, tile index, chessboard) that are useful in-game.
  const fromMainMenu = gameplay === 'main-menu';
  const TABS = fromMainMenu ? TABS_MAIN_MENU : TABS_IN_GAME;
  const [activeTab, setActiveTab] = useState<OptionsTab>(TABS[0]);
  const [capturing, setCapturing] = useState<CapturingTarget | null>(null);
  const setIsCapturingInput = useGameStore((s) => s.setIsCapturingInput);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const startCapture = useInputCapture();

  const interfaceEntries = useMemo(
    () =>
      Object.entries(CONTROLS_CONFIG.interface) as [
        string,
        { displayName: string; description: string },
      ][],
    [],
  );
  const dungeonEntries = useMemo(
    () =>
      Object.entries(CONTROLS_CONFIG.dungeon) as [
        string,
        { displayName: string; description: string },
      ][],
    [],
  );

  const DUNGEON_START = interfaceEntries.length;
  const RESET_ROW = interfaceEntries.length + dungeonEntries.length;

  // Sync capturing state to store for useGameInput
  useEffect(() => {
    setIsCapturingInput(capturing !== null);
  }, [capturing, setIsCapturingInput]);

  // Cancel from options → return to whatever opened it. From the MainMenu there
  // is no EscapeMenu in the background, so just close the interface.
  const closeOptions = useCallback(() => {
    setCapturing(null);
    setIsCapturingInput(false);
    if (fromMainMenu) {
      closeInterface();
    } else {
      openInterface('escape-menu');
    }
  }, [fromMainMenu, setIsCapturingInput]);

  // Pause keypress from options → close all interfaces, resume gameplay.
  const closeAll = useCallback(() => {
    setCapturing(null);
    setIsCapturingInput(false);
    closeInterface();
  }, [setIsCapturingInput]);

  useEffect(() => {
    if (!capturing) {
      return;
    }

    const onEscape = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        setCapturing(null);
      }
    };
    document.addEventListener('keydown', onEscape, true);

    const onCapture = (raw: RawInput) => {
      if (capturing.inputType === 'keyboard') {
        if (raw.kind !== 'keyboard') {
          startCapture(onCapture);
          return;
        }
        const binding: KeyboardBinding = {
          code: raw.code,
          modifiers: raw.modifiers.length > 0 ? raw.modifiers : undefined,
        };
        if (capturing.context === 'interface') {
          mapping.setInterfaceKeyboard(
            capturing.action as InterfaceAction,
            binding,
          );
        } else {
          mapping.setDungeonKeyboard(
            capturing.action as DungeonAction,
            binding,
          );
        }
        setCapturing(null);
      } else {
        if (raw.kind !== 'gamepad') {
          startCapture(onCapture);
          return;
        }
        const binding: GamepadBinding = {
          type: 'button',
          index: raw.index,
          label: GAMEPAD_BUTTON_LABELS[raw.index] ?? `Button ${raw.index}`,
        };
        if (capturing.context === 'interface') {
          mapping.setInterfaceGamepad(
            capturing.action as InterfaceAction,
            binding,
          );
        } else {
          mapping.setDungeonGamepad(capturing.action as DungeonAction, binding);
        }
        setCapturing(null);
      }
    };
    startCapture(onCapture);

    return () => {
      document.removeEventListener('keydown', onEscape, true);
    };
  }, [capturing, mapping, startCapture]);

  const adjustVolume = (id: string, delta: number) => {
    const next = (current: number) => Math.max(0, Math.min(1, current + delta));
    if (id === 'volume_master') {
      setMasterVolume(next(masterVolume));
    } else if (id === 'volume_music') {
      setMusicVolume(next(musicVolume));
    } else if (id === 'volume_sfx') {
      setSfxVolume(next(sfxVolume));
    }
  };

  const { containerRef, setFocus } = useMenuNavigation({
    onConfirm: (id) => {
      if (showResetConfirm) {
        mapping.resetAllControls();
        setShowResetConfirm(false);
        return;
      }
      if (id === 'gamepad' || id === 'keyboard' || id === 'keyboard-mouse') {
        applyPreset(id as InputPresetId);
        return;
      }
      if (id === 'devtool') {
        setDevModeEnabled(!devModeEnabled);
        return;
      }
      if (id === 'tileIndex') {
        setShowTileIndex(!showTileIndex);
        return;
      }
      if (id === 'chessboard') {
        setShowChessboard(!showChessboard);
        return;
      }
      if (id === 'coordinates') {
        setShowCoordinates(!showCoordinates);
        return;
      }
      if (id.startsWith('hotkey-')) {
        // Read-only rows: navigable (to scroll the list) but no action.
        return;
      }
      if (id === 'reset') {
        setShowResetConfirm(true);
        return;
      }
      // Parse "context:action:inputType"
      const parts = id.split(':');
      if (parts.length === 3) {
        const inputType = parts[2] as 'keyboard' | 'gamepad';
        // Guard: do not allow capturing a keyboard key when the last device
        // used is the gamepad (and vice-versa). Otherwise the user cannot
        // "confirm" the cell (CONFIRM would come through the incompatible
        // device) and stays stuck in capture mode.
        const lastSource = useInputSourceStore.getState().lastSource;
        if (
          (lastSource === 'gamepad' && inputType === 'keyboard') ||
          (lastSource === 'keyboard' && inputType === 'gamepad')
        ) {
          return;
        }
        setCapturing({
          context: parts[0] as GameplayContext,
          action: parts[1],
          inputType,
        });
      }
    },
    onLeft: (id) => adjustVolume(id, -VOLUME_STEP),
    onRight: (id) => adjustVolume(id, VOLUME_STEP),
    onCancel: () => {
      if (showResetConfirm) {
        setShowResetConfirm(false);
        return;
      }
      closeOptions();
    },
    onPause: () => {
      if (showResetConfirm) {
        setShowResetConfirm(false);
        return;
      }
      closeAll();
    },
    onTabNext: () => {
      if (showResetConfirm) {
        return;
      }
      setActiveTab((prev) => {
        const idx = TABS.indexOf(prev);
        return TABS[(idx + 1) % TABS.length];
      });
      setFocus(0, 0);
      setCapturing(null);
    },
    onTabPrev: () => {
      if (showResetConfirm) {
        return;
      }
      setActiveTab((prev) => {
        const idx = TABS.indexOf(prev);
        return TABS[(idx - 1 + TABS.length) % TABS.length];
      });
      setFocus(0, 0);
      setCapturing(null);
    },
    disabled: capturing !== null,
    frozen: showResetConfirm,
  });

  const isCellCapturing = (
    context: GameplayContext,
    action: string,
    inputType: 'keyboard' | 'gamepad',
  ) =>
    capturing?.context === context &&
    capturing.action === action &&
    capturing.inputType === inputType;

  const renderBindingRow = (
    context: GameplayContext,
    action: string,
    cfg: { displayName: string; description: string },
    globalRowIdx: number,
  ) => (
    <tr key={`${context}-${action}`}>
      <td className="col-name">{cfg.displayName}</td>
      <td className="col-desc">{cfg.description}</td>

      <td
        data-nav-row={globalRowIdx}
        data-nav-col={0}
        data-nav-id={`${context}:${action}:keyboard`}
        className="col-binding"
      >
        {isCellCapturing(context, action, 'keyboard') ? (
          <span className="binding-capture">Appuyez…</span>
        ) : (
          <KeyDisplay
            context={context as GameplayContext}
            action={action as never}
            size="md"
            forceSource="keyboard"
          />
        )}
      </td>

      <td
        data-nav-row={globalRowIdx}
        data-nav-col={1}
        data-nav-id={`${context}:${action}:gamepad`}
        className="col-binding"
      >
        {isCellCapturing(context, action, 'gamepad') ? (
          <span className="binding-capture">Appuyez…</span>
        ) : (
          <KeyDisplay
            context={context as GameplayContext}
            action={action as never}
            size="md"
            forceSource="gamepad"
          />
        )}
      </td>
    </tr>
  );

  return (
    <div className="options-overlay">
      <NineSliceFrame>
        <div className="options-panel" ref={containerRef}>
          <div className="options-header">
            <h2 className="options-title">Options</h2>
          </div>

          <div className="options-tabs">
            <span className="options-tabs-key options-tabs-key--prev">
              <KeyDisplay context="interface" action="TAB_PREV" size="sm" />
            </span>
            {!fromMainMenu && (
              <div
                className={`options-tab${
                  activeTab === 'general' ? ' active' : ''
                }`}
              >
                Général
              </div>
            )}
            <div
              className={`options-tab${activeTab === 'sound' ? ' active' : ''}`}
            >
              Son
            </div>
            <div
              className={`options-tab${
                activeTab === 'controls' ? ' active' : ''
              }`}
            >
              Contrôles
            </div>
            <span className="options-tabs-key options-tabs-key--next">
              <KeyDisplay context="interface" action="TAB_NEXT" size="sm" />
            </span>
          </div>

          <div className="options-content">
            {activeTab === 'general' && (
              <section className="options-section">
                <h3 className="options-section-title">Général</h3>
                <div
                  data-nav-row={0}
                  data-nav-id="chessboard"
                  className="options-checkbox"
                >
                  <span
                    className={`checkbox-indicator${
                      showChessboard ? ' checked' : ''
                    }`}
                  />
                  <span>Afficher l'échiquier</span>
                </div>
                <div
                  data-nav-row={1}
                  data-nav-id="devtool"
                  className="options-checkbox"
                >
                  <span
                    className={`checkbox-indicator${
                      devModeEnabled ? ' checked' : ''
                    }`}
                  />
                  <span>Dev mode</span>
                </div>

                {devModeEnabled && (
                  <>
                    <div
                      data-nav-row={2}
                      data-nav-id="tileIndex"
                      className="options-checkbox"
                    >
                      <span
                        className={`checkbox-indicator${
                          showTileIndex ? ' checked' : ''
                        }`}
                      />
                      <span>Afficher l'index des tuiles</span>
                    </div>
                    <div
                      data-nav-row={3}
                      data-nav-id="coordinates"
                      className="options-checkbox"
                    >
                      <span
                        className={`checkbox-indicator${
                          showCoordinates ? ' checked' : ''
                        }`}
                      />
                      <span>Afficher les coordonnées</span>
                    </div>

                    <h3 className="options-section-title">Raccourcis dev</h3>
                    {DEV_HOTKEYS.map((hk, i) => (
                      <div
                        key={hk.keys}
                        data-nav-row={4 + i}
                        data-nav-id={`hotkey-${i}`}
                        className="options-hotkey-row"
                      >
                        <span className="options-hotkey-keys">{hk.keys}</span>
                        <span className="options-hotkey-label">{hk.label}</span>
                      </div>
                    ))}
                  </>
                )}
              </section>
            )}

            {activeTab === 'sound' && (
              <section className="options-section">
                {[
                  {
                    id: 'volume_master',
                    label: 'Volume général',
                    value: masterVolume,
                  },
                  { id: 'volume_music', label: 'Musique', value: musicVolume },
                  {
                    id: 'volume_sfx',
                    label: 'Effets sonores',
                    value: sfxVolume,
                  },
                ].map((row, i) => (
                  <div
                    key={row.id}
                    data-nav-row={i}
                    data-nav-id={row.id}
                    className="options-slider"
                  >
                    <span className="options-slider-label">{row.label}</span>
                    <span className="options-slider-right">
                      <TrackBar value={row.value} />
                      <span className="options-slider-value">
                        {Math.round(row.value * 100)}%
                      </span>
                    </span>
                  </div>
                ))}
              </section>
            )}

            {activeTab === 'controls' && (
              <section className="options-section options-presets-section">
                <h3 className="options-section-title">Préréglages</h3>
                <div className="options-presets-row">
                  {(
                    ['gamepad', 'keyboard', 'keyboard-mouse'] as InputPresetId[]
                  ).map((id, i) => (
                    <div
                      key={id}
                      className="options-preset-btn"
                      data-nav-row={-1}
                      data-nav-col={i}
                      data-nav-id={id}
                    >
                      <span className="options-preset-btn-label">
                        {INPUT_PRESETS[id].label}
                      </span>
                      <span className="options-preset-btn-desc">
                        {INPUT_PRESETS[id].description}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="options-presets-mouse">
                  Souris :{' '}
                  <span className="options-presets-mouse-state">
                    {mouseControlEnabled ? 'activée' : 'désactivée'}
                  </span>
                </p>
              </section>
            )}
            {activeTab === 'controls' && (
              <table className="options-table">
                <thead>
                  <tr>
                    <th className="col-name">Action</th>
                    <th className="col-desc">Description</th>
                    <th className="col-binding">Clavier</th>
                    <th className="col-binding">Manette</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="options-table-section">
                    <td colSpan={4}>Interface</td>
                  </tr>
                  {interfaceEntries.map(([action, cfg], i) =>
                    renderBindingRow('interface', action, cfg, i),
                  )}
                  <tr className="options-table-section">
                    <td colSpan={4}>Donjon</td>
                  </tr>
                  {dungeonEntries.map(([action, cfg], i) =>
                    renderBindingRow('dungeon', action, cfg, DUNGEON_START + i),
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="options-footer">
            {activeTab === 'controls' && (
              <div
                data-nav-row={RESET_ROW}
                data-nav-id="reset"
                className="reset-all-btn"
              >
                Réinitialiser tout
              </div>
            )}
            <div className="close-btn">
              <KeyDisplay context="interface" action="CANCEL" size="md" />
              Fermer
            </div>
          </div>
        </div>
      </NineSliceFrame>

      {showResetConfirm && (
        <div className="reset-confirm-overlay">
          <NineSliceFrame>
            <div className="reset-confirm-dialog">
              <p className="reset-confirm-text">
                Réinitialiser tous les contrôles aux valeurs par défaut ?
              </p>
              <div className="reset-confirm-actions">
                <span className="reset-confirm-btn">
                  <KeyDisplay context="interface" action="CONFIRM" size="md" />
                  Confirmer
                </span>
                <span className="reset-confirm-btn">
                  <KeyDisplay context="interface" action="CANCEL" size="md" />
                  Annuler
                </span>
              </div>
            </div>
          </NineSliceFrame>
        </div>
      )}
    </div>
  );
};
